/**
 * Atlas PM · 任务表单 · 任务形态下拉选项（P8.6）
 *
 * 数据源：`GET /api/task-categories`（登录即可，无需 `dict:manage`），
 * 只返回启用中的字典项。设置页仍走 `/api/system/task-categories`。
 *
 * ⚠️ query key 与设置页的 `['settings','task-categories']` **刻意区分**，
 *    避免任务表单复用设置页缓存、从而被迫请求需要 `dict:manage` 的接口。
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { TaskCategoryItem } from '../settings/task-categories/api';

/** 任务表单专用 query key（与设置页缓存互不影响） */
export const taskCategoryOptionKeys = {
  active: ['task-form', 'task-categories', 'active'] as const,
};

export interface TaskCategoryOption {
  value: string;
  label: string;
  /** ⭐ P8.9 备注说明：选中后在任务形态下方作为提示展示 */
  description: string | null;
}

/**
 * 拉取启用中的任务形态，并生成下拉选项。
 *
 * @param currentId   当前表单值（编辑存量任务时可能指向已停用项）
 * @param currentName 当前值的名称（来自任务详情）；停用项不在公开列表中，
 *                    用它回显「名称（已停用）」，取不到时才退化为 `形态 #id`。
 */
export function useTaskCategoryOptions(
  currentId?: string,
  currentName?: string | null,
) {
  const query = useQuery({
    queryKey: taskCategoryOptionKeys.active,
    queryFn: () => api.get<TaskCategoryItem[]>('/task-categories'),
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo<TaskCategoryOption[]>(() => {
    const list = query.data ?? [];
    const active = list
      .filter((c) => c.isActive)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id))
      .map((c) => ({ value: c.id, label: c.name, description: c.description ?? null }));

    const id = currentId;
    if (id && !active.some((o) => o.value === id)) {
      active.push({
        value: id,
        label: currentName ? `${currentName}（已停用）` : `形态 #${id}`,
        description: null,
      });
    }
    return active;
  }, [query.data, currentId, currentName]);

  return { query, options };
}
